import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

/**
 * Authorization check.
 *
 * IMPORTANT: This hook distinguishes three distinct states so the UI can
 * react correctly instead of dumping every failure into "Access Denied":
 *
 *   - isLoading      : queries still running
 *   - isError        : the authorization RPCs themselves failed (network,
 *                      transient JWT/refresh races, 5xx). The caller is NOT
 *                      necessarily unauthorized — the server never gave us
 *                      a definitive answer. UI should show a retryable
 *                      error, NOT redirect to /unauthorized.
 *   - isAuthorized=false (and !isError): the server answered and the user
 *                      has no role and no allowed email domain. This is the
 *                      only state where /unauthorized is the correct screen.
 */
export function useAuthorization() {
  const { user } = useAuth();

  const { data: isAuthorized, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["user-authorization", user?.id, user?.email],
    queryFn: async () => {
      if (!user?.id || !user?.email) return false;

      // Check if user has any role assigned. THROW on error so React Query
      // surfaces isError instead of silently treating an RPC failure as
      // "no role" (which would bounce the user to /unauthorized).
      const { data: role, error: roleErr } = await supabase.rpc("get_user_role", {
        _user_id: user.id,
      });
      if (roleErr) throw roleErr;
      if (role) return true;

      // Check if user's email domain is in allowed list
      const { data: domainAllowed, error: domainErr } = await supabase.rpc(
        "is_email_domain_allowed",
        { _email: user.email },
      );
      if (domainErr) throw domainErr;

      return !!domainAllowed;
    },
    enabled: !!user?.id && !!user?.email,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
    staleTime: 60_000,
  });

  return {
    isAuthorized: isAuthorized === true,
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
  };
}
